import { useMemo } from 'react'

import { matchFilterArrayIncludes, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { LayoutTemplateModel, LayoutTemplateRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['layoutTemplates']

type LayoutTemplateFilters = {
  compatible_product_id?: number,
  publication_id?: number,
}

type UseLayoutTemplatesOptions = {
  filters?: LayoutTemplateFilters,
  performHttpRequests?: boolean,
  requestOptions?: LayoutTemplateRequestOptions,
}

function useLayoutTemplates(options: UseLayoutTemplatesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    compatible_product_id: filterCompatibleProductId,
    publication_id: filterPublicationId,
  } = filters

  const {
    updatedEntities: { layoutTemplates: layoutTemplatesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { layoutTemplates } = useSelector(reduxState => reduxState.entities)

  const filteredLayoutTemplates = useMemo(() => {
    const filtered = Object.values(layoutTemplates).filter((layoutTemplate: LayoutTemplateModel) => {
      const { compatible_product_ids: compatibleProductIds = [], permission_ids } = layoutTemplate
      const { publications: publicationIds = [] } = permission_ids || {}

      const matchCompatibleProductId = matchFilterArrayIncludes(
        compatibleProductIds,
        filterCompatibleProductId ? Number(filterCompatibleProductId) : undefined,
      )

      const matchPublicationId = matchFilterArrayIncludes(
        publicationIds,
        filterPublicationId ? Number(filterPublicationId) : undefined,
      )

      return matchCompatibleProductId && matchPublicationId
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [layoutTemplatesUpdatedAt, JSON.stringify(filters)])

  const filteredLayoutTemplatesCount = filteredLayoutTemplates.length
  const hasFilteredLayoutTemplates = !!filteredLayoutTemplatesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredLayoutTemplatesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingLayoutTemplates } = useReduxAction(
    'layoutTemplates',
    'loadLayoutTemplates',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredLayoutTemplates,
    filteredLayoutTemplatesCount,
    hasFilteredLayoutTemplates,
    loading: loadingLayoutTemplates,
  }
}

export default useLayoutTemplates
